import {
  type ClinicalNoteInput,
  ClinicalNoteListResponseSchema,
  ClinicalNoteSchema,
  type MedicalProfileInput,
  MedicalProfileSchema,
  type PatientInput,
  PatientListResponseSchema,
  PatientSchema,
} from '@clinic/contracts';
import { createInfiniteQuery, createQuery, useQueryClient } from '@tanstack/svelte-query';
import { createMutation } from '../../../shared/api/mutation';
import { apiRequest } from '../../../shared/api/client';
import { type MaybeGetter, read } from '../../../shared/lib/maybe';
import { keys } from '../../../shared/api/keys';

export function usePatients(orgId: string, q: MaybeGetter<string>, archived: MaybeGetter<boolean> = false, limit: MaybeGetter<number> = 30, enabled: MaybeGetter<boolean> = true) {
  return createInfiniteQuery(() => ({
    queryKey: [...keys.patients(orgId), 'list', { q: read(q), archived: read(archived), limit: read(limit) }],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({ limit: String(read(limit)), status: read(archived) ? 'archived' : 'active' });
      if (read(q)) params.set('q', read(q));
      if (pageParam) params.set('cursor', pageParam);
      return apiRequest(`/v1/clinic/patients?${params}`, { orgId, schema: PatientListResponseSchema, signal });
    },
    getNextPageParam: (last) => last.nextCursor,
    enabled: read(enabled),
  }));
}

export function usePatient(orgId: string, id: MaybeGetter<string>, enabled: MaybeGetter<boolean> = true) {
  return createQuery(() => ({
    queryKey: keys.patient(orgId, read(id)),
    queryFn: ({ signal }) => apiRequest(`/v1/clinic/patients/${read(id)}`, { orgId, schema: PatientSchema, signal }),
    enabled: read(enabled) && read(id) !== '',
  }));
}

export function useCreatePatient(orgId: string) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: PatientInput) =>
      apiRequest('/v1/clinic/patients', { method: 'POST', body, orgId, schema: PatientSchema }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.patients(orgId) }),
  }));
}

/**
 * A new patient with the medical profile filled in the same form. Two requests (the create endpoint
 * takes no profile): if the second fails the patient is already saved, so the failure comes back
 * beside the patient instead of being thrown, and the caller shows it with a way to retry.
 */
export function useCreatePatientWithMedical(orgId: string) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: async ({ patient, medical }: { patient: PatientInput; medical: MedicalProfileInput | null }) => {
      const created = await apiRequest('/v1/clinic/patients', { method: 'POST', body: patient, orgId, schema: PatientSchema });
      let medicalError: unknown = null;
      if (medical) {
        try {
          const profile = await apiRequest(`/v1/clinic/patients/${created.id}/medical-profile`, {
            method: 'PUT',
            body: medical,
            orgId,
            schema: MedicalProfileSchema,
          });
          qc.setQueryData(keys.medical(orgId, created.id), profile);
        } catch (e) {
          medicalError = e;
        }
      }
      return { patient: created, medicalError };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.patients(orgId) }),
  }));
}

export function useUpdatePatient(orgId: string, id: MaybeGetter<string>) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: PatientInput) =>
      apiRequest(`/v1/clinic/patients/${read(id)}`, { method: 'PUT', body, orgId, schema: PatientSchema }),
    onSuccess: (patient) => {
      qc.setQueryData(keys.patient(orgId, read(id)), patient);
      return qc.invalidateQueries({ queryKey: keys.patients(orgId) });
    },
  }));
}

export function useSetPatientArchived(orgId: string, id: MaybeGetter<string>) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (archived: boolean) =>
      apiRequest(`/v1/clinic/patients/${read(id)}/${archived ? 'archive' : 'restore'}`, {
        method: 'POST',
        orgId,
        schema: PatientSchema,
      }),
    onSuccess: (patient) => {
      qc.setQueryData(keys.patient(orgId, read(id)), patient);
      return qc.invalidateQueries({ queryKey: keys.patients(orgId) });
    },
  }));
}

// ---- clinical (audited reads: fetched once per open, never refetched in the background) ----

const AUDITED = { staleTime: Number.POSITIVE_INFINITY, gcTime: 60_000, retry: false } as const;

export function useMedicalProfile(orgId: string, patientId: MaybeGetter<string>, enabled: MaybeGetter<boolean>) {
  return createQuery(() => ({
    queryKey: keys.medical(orgId, read(patientId)),
    queryFn: ({ signal }) =>
      apiRequest(`/v1/clinic/patients/${read(patientId)}/medical-profile`, { orgId, schema: MedicalProfileSchema, signal }),
    enabled: read(enabled),
    ...AUDITED,
  }));
}

export function useSaveMedicalProfile(orgId: string, patientId: MaybeGetter<string>) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: MedicalProfileInput) =>
      apiRequest(`/v1/clinic/patients/${read(patientId)}/medical-profile`, {
        method: 'PUT',
        body,
        orgId,
        schema: MedicalProfileSchema,
      }),
    onSuccess: (profile) => qc.setQueryData(keys.medical(orgId, read(patientId)), profile),
  }));
}

export function useNotes(orgId: string, patientId: MaybeGetter<string>, enabled: MaybeGetter<boolean>) {
  return createQuery(() => ({
    queryKey: keys.notes(orgId, read(patientId)),
    queryFn: ({ signal }) =>
      apiRequest(`/v1/clinic/patients/${read(patientId)}/notes`, { orgId, schema: ClinicalNoteListResponseSchema, signal }),
    enabled: read(enabled),
    ...AUDITED,
  }));
}

export function useAddNote(orgId: string, patientId: MaybeGetter<string>) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: ClinicalNoteInput) =>
      apiRequest(`/v1/clinic/patients/${read(patientId)}/notes`, {
        method: 'POST',
        body,
        orgId,
        schema: ClinicalNoteSchema,
      }),
    // Prepend instead of refetching: a refetch would be another audited read.
    onSuccess: (note) =>
      qc.setQueryData(keys.notes(orgId, read(patientId)), (old: { items: (typeof note)[] } | undefined) => ({
        items: [note, ...(old?.items ?? [])],
      })),
  }));
}
